/**
 * Shared photo widget for JoiningDetailPage.
 *
 * Real photo upload + camera capture pipeline:
 *  1. Binary upload  → POST /hr/employees/{id}/photo  (multipart, ≤5MB,
 *     jpg/png/webp). Backend saves to backend/static/employee-photos
 *     and updates employee.photo_path. Served back via /static.
 *  2. Metadata sync  → POST /joining/{id}/photo with photo_path + source
 *     so photo_status flips to UPLOADED, photo_source recorded, and
 *     joining_audit_log gets a PHOTO_UPLOAD event.
 *  3. Verification   → PATCH /joining/{id}/photo/verify (HR approve/reject)
 *
 * Two capture modes:
 *  - "Upload File"  — system file picker (laptop/phone gallery)
 *  - "Use Camera"   — getUserMedia → <video> → Canvas snapshot → Blob upload
 */
import { useEffect, useRef, useState } from 'react'
import {
  Avatar,
  Button,
  Card,
  Modal,
  Input,
  Space,
  Tag,
  Upload,
  message,
} from 'antd'
import {
  UserOutlined,
  UploadOutlined,
  CameraOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { RcFile } from 'antd/es/upload'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { joiningApi, type JoiningDetail, type PhotoStatus } from '@/features/hr/joiningApi'

interface Props { detail: JoiningDetail }

const STATUS_COLOR: Record<PhotoStatus, string> = {
  NOT_UPLOADED: 'default',
  UPLOADED: 'blue',
  VERIFIED: 'green',
  REJECTED: 'red',
  REUPLOAD_REQUIRED: 'orange',
}

// Accept a big phone photo (up to 10 MB) — the server compresses it to
// ~150 KB on save, so disk stays tiny. Accepts HEIC (iPhone default).
const MAX_BYTES = 10 * 1024 * 1024  // 10 MB in → ~150 KB stored
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]

/**
 * Upload a Blob/File to the existing /hr/employees/{id}/photo endpoint,
 * then sync the joining metadata. Returns the new photo_path.
 */
async function uploadPhotoBinary(
  employeeId: string,
  file: Blob,
  filename: string,
  source: 'FILE_UPLOAD' | 'CAMERA_CAPTURE',
): Promise<string> {
  const form = new FormData()
  form.append('file', file, filename)
  const { data } = await apiClient.post(`/hr/employees/${employeeId}/photo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  const photoPath = (data?.photo_path as string | undefined) ?? `employee-photos/${employeeId}.jpg`
  // Sync joining metadata (photo_status, source, audit log)
  await joiningApi.recordPhoto(employeeId, photoPath, source)
  return photoPath
}

export default function PhotoUploadCard({ detail }: Props) {
  const qc = useQueryClient()
  const [rejectOpen, setRejectOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [cameraOpen, setCameraOpen] = useState(false)

  const employee = detail.employee as Record<string, unknown>
  const photoStatus = (employee.photo_status as PhotoStatus) || 'NOT_UPLOADED'
  const photoPath = (employee.photo_path as string | null) || null
  const photoUrl = (employee.photo_url as string | null) || (photoPath ? `/static/${photoPath}` : null)
  const isActive = detail.is_active

  // Cache-bust the avatar URL so a freshly uploaded photo replaces the old one
  const [bust, setBust] = useState(0)
  const avatarSrc = photoUrl ? `${photoUrl}?v=${bust || Date.now()}` : undefined

  const uploadMutation = useMutation({
    mutationFn: async ({ file, source }: { file: File | Blob; source: 'FILE_UPLOAD' | 'CAMERA_CAPTURE' }) => {
      const filename =
        file instanceof File
          ? file.name
          : `capture-${Date.now()}.jpg`
      return uploadPhotoBinary(detail.id, file, filename, source)
    },
    onSuccess: () => {
      message.success('Photo uploaded')
      setBust(Date.now())
      void qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err.response?.data?.detail ?? 'Upload failed')
    },
  })

  const verifyPhoto = useMutation({
    mutationFn: (args: { verified: boolean; reason?: string }) =>
      joiningApi.verifyPhoto(detail.id, args.verified, args.reason),
    onSuccess: () => {
      message.success('Photo updated')
      setRejectOpen(false)
      setReason('')
      void qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  // Validate before upload (extension + size)
  const validate = (file: RcFile): boolean => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error('Only JPG, PNG or WEBP images are allowed')
      return false
    }
    if (file.size > MAX_BYTES) {
      message.error(`File too large (${Math.round(file.size / 1024 / 1024)} MB). Max 10 MB.`)
      return false
    }
    return true
  }

  return (
    <Card size="small" title="Employee Photo">
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <Avatar
          size={140}
          src={avatarSrc}
          icon={<UserOutlined />}
          style={{ border: '2px solid #f0f0f0' }}
        />
      </div>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <Tag color={STATUS_COLOR[photoStatus]}>{photoStatus.replace(/_/g, ' ')}</Tag>
      </div>
      {isActive && photoStatus === 'VERIFIED' && (
        <Tag color="warning" style={{ display: 'block', textAlign: 'center', marginBottom: 12 }}>
          Active employee — photo change is audited
        </Tag>
      )}
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Upload
          accept="image/jpeg,image/png,image/webp"
          showUploadList={false}
          beforeUpload={(file) => {
            if (!validate(file)) return Upload.LIST_IGNORE
            uploadMutation.mutate({ file, source: 'FILE_UPLOAD' })
            return false  // prevent AntD's default XHR
          }}
        >
          <Button
            icon={<UploadOutlined />}
            block
            loading={uploadMutation.isPending && uploadMutation.variables?.source === 'FILE_UPLOAD'}
          >
            Upload from File
          </Button>
        </Upload>

        <Button
          icon={<CameraOutlined />}
          block
          onClick={() => setCameraOpen(true)}
        >
          Use Camera
        </Button>

        {photoStatus === 'UPLOADED' && (
          <Space style={{ width: '100%', marginTop: 8 }}>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={verifyPhoto.isPending}
              onClick={() => verifyPhoto.mutate({ verified: true })}
            >
              Verify
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={() => setRejectOpen(true)}
            >
              Reject
            </Button>
          </Space>
        )}
      </Space>

      <Modal
        title="Reject Photo"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={() => verifyPhoto.mutate({ verified: false, reason })}
        confirmLoading={verifyPhoto.isPending}
        okButtonProps={{ danger: true, disabled: !reason.trim() }}
        okText="Reject"
      >
        <p>Reason will be logged in the audit trail and the worker will need to re-upload.</p>
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. photo is blurry / face not visible / wrong person"
        />
      </Modal>

      <CameraCaptureModal
        open={cameraOpen}
        onCancel={() => setCameraOpen(false)}
        onCapture={(blob) => {
          uploadMutation.mutate({ file: blob, source: 'CAMERA_CAPTURE' })
          setCameraOpen(false)
        }}
      />
    </Card>
  )
}

/**
 * Camera capture modal — opens device webcam, lets user snap a photo,
 * preview, retake or confirm. Stops the stream on close to free camera.
 */
interface CameraCaptureModalProps {
  open: boolean
  onCancel: () => void
  onCapture: (blob: Blob) => void
}

function CameraCaptureModal({ open, onCancel, onCapture }: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null)

  // Start / stop camera as the modal opens / closes
  useEffect(() => {
    if (!open) {
      cleanup()
      return
    }
    let cancelled = false
    setError(null)
    setPreviewUrl(null)
    setCapturedBlob(null)
    ;(async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera API not available in this browser')
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play()
        }
      } catch (e: unknown) {
        const msg =
          (e as { name?: string; message?: string })?.name === 'NotAllowedError'
            ? 'Camera permission denied. Please allow camera access in your browser settings.'
            : (e as { message?: string })?.message ?? 'Could not start camera'
        setError(msg)
      }
    })()
    return () => {
      cancelled = true
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function cleanup() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
  }

  const takeShot = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return
    const w = video.videoWidth
    const h = video.videoHeight
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, w, h)
    canvas.toBlob(
      (blob) => {
        if (!blob) return
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        setPreviewUrl(URL.createObjectURL(blob))
        setCapturedBlob(blob)
      },
      'image/jpeg',
      0.92,
    )
  }

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    setCapturedBlob(null)
  }

  return (
    <Modal
      title={
        <Space>
          <CameraOutlined /> Capture Employee Photo
        </Space>
      }
      open={open}
      onCancel={onCancel}
      width={720}
      destroyOnClose
      footer={
        previewUrl ? (
          <Space>
            <Button icon={<ReloadOutlined />} onClick={retake}>
              Retake
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => capturedBlob && onCapture(capturedBlob)}
            >
              Use This Photo
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              icon={<CameraOutlined />}
              onClick={takeShot}
              disabled={!!error}
            >
              Capture
            </Button>
          </Space>
        )
      }
    >
      {error ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <CloseOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
          <p style={{ marginTop: 12, color: '#666' }}>{error}</p>
          <p style={{ fontSize: 12, color: '#888' }}>
            Tip: in Chrome, click the camera icon in the address bar to grant permission, then
            close and reopen this dialog.
          </p>
        </div>
      ) : previewUrl ? (
        <div style={{ textAlign: 'center' }}>
          <img
            src={previewUrl}
            alt="captured"
            style={{ maxWidth: '100%', maxHeight: 480, borderRadius: 8 }}
          />
        </div>
      ) : (
        <div style={{ textAlign: 'center', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', maxHeight: 480, display: 'block' }}
          />
        </div>
      )}
      {/* Off-screen canvas for snapshot rendering */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Modal>
  )
}
